/*
 *   Collects a set of options that are used to populate a new time card in the Timekeeping Entries modal
 */
import { createRef, FC, useEffect, useState } from "react"
import { useSelector } from "react-redux"
import styled from "styled-components"
import SelectorWrapper from "../../SelectorWrapper"
import { referenceablesToValueFormatters } from "../../common/referenceable-value-formatters"
import { getDateTimeFormats } from "../../common/ts-utils"
import { tCurrentUserEmployee, tFeatures } from "../../common/types"
import format from "date-fns-tz/format"
import {
    Checkbox,
    colorAttentionBlue50,
    DatePicker,
    FormFieldGroup,
    Label,
    StandardModal,
} from "@rhumbix/rmbx_design_system_web"
import { tContext, tGroupKeyInfo } from "../custom-dashboards/types"
import { adminAndPmRoles, DATE_ONLY_FORMAT, STORED_DATE_ONLY_FORMAT } from "../../common/constants"
import { MutableSourceData, tSourceData } from "../../dashboard-data/types"
import { ColumnApi } from "ag-grid-community"
import {
    CREATE_TIME_CARD_MODAL_CANCELLED,
    CREATE_TIME_CARD_MODAL_COMPLETED,
    logUserAmplitudeEvent,
} from "../../common/amplitude-event-logging"
import { tFilterState } from "../../filters/types"
import {
    EmployeeHolidayRecord,
    EmployeePTORecord,
    iCohort,
    iCohortEmployee,
    iEmployee,
    iTimelineEntryVersion,
    tCompanyStartStopType,
    tEntitySelection,
    tProject,
} from "../../cached-data/types"
import * as API from "../../api"
import {
    getSettingsForShiftExtraSchema,
    populateShiftExtraCostCodeColDef,
} from "../../new-custom-forms/utils/custom-table-utils"
import { iEmployeeWorkShiftFormSchema } from "../../cached-data/types"
import { getObjectId } from "../../common/ag-grid-ts-utils"
import { loadEntities } from "../../cached-data/actions"
import cloneDeep from "clone-deep"
import { addDays, endOfDay, startOfDay } from "date-fns"
import { getFlagEnabled } from "../../getFlagValue"
import {
    AbsencePTORowFields,
    buildPTORowFields,
    TimekeepingPTORowFields,
} from "../custom-dashboards/settings-files/pto-requests"
import { selectPTOFulfillment } from "../../selectors/pto"

type ContextFields = {
    foreman: number
    shift_start_time: Date | null
    shift_end_time: Date | null
    work_shift_id: Record<string, unknown>
}

/**
 * Given PTO/holiday records for a single employee on a single date, and API-fetched cost codes
 * for the selected projects (keyed by pto_bucket_id), build the initial absence and
 * timekeeping-entry rows that fulfill those requests.
 *
 * Rules:
 *   - ABSENCE_TYPE records produce one absence row per record.
 *   - COST_CODE records are matched against `ptoCostCodesByBucket`:
 *       - 0 matches → no row by default; row with cost_code null if includeUnresolvableCostCodeRows is true
 *       - 1 match  → TK row with that cost code pre-filled
 *       - 2+ matches → TK row with cost_code null (user must choose)
 *
 * @param ptoForEmployee - PTO and holiday records for a single employee on the shift date.
 * @param baseFields - Common fields shared by every generated row.
 * @param ptoCostCodesByBucket - API-fetched cost codes indexed by pto_bucket_id.
 * @param includeUnresolvableCostCodeRows - When true, COST_CODE records with 0 resolvable cost
 *   codes still produce a TK row with cost_code null. Use in flows with a confirmation modal
 *   where the user can fill in the missing cost code (e.g. Duplicate).
 * @returns Object with `absences` and `timekeepingEntries` arrays of partial row objects.
 */
export const buildPTORowsForEmployee = (
    ptoForEmployee: Array<EmployeePTORecord | EmployeeHolidayRecord>,
    baseFields: {
        foreman: number
        shiftStartTime: Date | null
        shiftEndTime: Date | null
        workShift: Record<string, unknown>
    },
    ptoCostCodesByBucket: Map<number, Array<{ id: number }>> = new Map(),
    includeUnresolvableCostCodeRows = false
): {
    absences: Array<AbsencePTORowFields & ContextFields & { isPlaceholder: false }>
    timekeepingEntries: Array<
        TimekeepingPTORowFields & ContextFields & { cost_code: number | null; isPlaceholder: false }
    >
} => {
    const absences: Array<AbsencePTORowFields & ContextFields & { isPlaceholder: false }> = []
    const timekeepingEntries: Array<
        TimekeepingPTORowFields & ContextFields & { cost_code: number | null; isPlaceholder: false }
    > = []

    ptoForEmployee.forEach(record => {
        const result = buildPTORowFields(record)
        const contextFields = {
            foreman: baseFields.foreman,
            shift_start_time: baseFields.shiftStartTime,
            shift_end_time: baseFields.shiftEndTime,
            work_shift_id: baseFields.workShift,
        }
        if (result.rowType === "absence") {
            absences.push({ ...result.fields, ...contextFields, isPlaceholder: false })
        } else {
            const matchingCostCodes = ptoCostCodesByBucket.get(record.pto_bucket_id) ?? []
            if (matchingCostCodes.length === 0 && !includeUnresolvableCostCodeRows) return
            const resolvedCostCode = matchingCostCodes.length === 1 ? matchingCostCodes[0] : null
            timekeepingEntries.push({
                ...result.fields,
                ...contextFields,
                cost_code: resolvedCostCode?.id ?? null,
                isPlaceholder: false,
            })
        }
    })

    return { absences, timekeepingEntries }
}

/**
 * Builds a Map from pto_bucket_id to matching cost codes for all COST_CODE bucket PTO records.
 *
 * Cost codes already selected in the Create Timecard form are pre-seeded into the map by their
 * pto_bucket_id. Only bucket IDs not covered by the user's selection are fetched from the API,
 * keeping the user's explicit choices authoritative.
 *
 * @param ptoRecords - All PTO and holiday records for the shift.
 * @param projectIds - IDs of the projects selected in the Create Timecard form.
 * @param selectedCostCodes - Cost codes the user selected; those with a pto_bucket_id are
 *   pre-seeded into the result so no API call is made for those buckets.
 * @returns Map from pto_bucket_id to Array of cost code objects with `id`.
 */
export const buildPTOCostCodesByBucket = async (
    ptoRecords: Array<EmployeePTORecord | EmployeeHolidayRecord>,
    projectIds: number[],
    selectedCostCodes: Array<{ id: number; pto_bucket_id?: number | null }> = []
): Promise<Map<number, Array<{ id: number }>>> => {
    const result: Map<number, Array<{ id: number }>> = new Map()

    // Pre-seed with cost codes the user already selected, grouped by pto_bucket_id.
    for (const cc of selectedCostCodes) {
        if (cc.pto_bucket_id != null) {
            const existing = result.get(cc.pto_bucket_id) ?? []
            existing.push(cc)
            result.set(cc.pto_bucket_id, existing)
        }
    }

    if (projectIds.length === 0) return result

    // Only query the API for bucket IDs not already covered by the user's selection.
    const bucketIdsNeedingLookup = [
        ...new Set(
            ptoRecords
                .filter(r => r.pto_bucket_type === "COST_CODE" && !result.has(r.pto_bucket_id))
                .map(r => r.pto_bucket_id)
        ),
    ]
    if (bucketIdsNeedingLookup.length === 0) return result

    const searches = await Promise.all(
        bucketIdsNeedingLookup.map(
            bucketId =>
                API.getDataFromSearchv4("", "costCodes", {
                    project_id: projectIds,
                    pto_bucket_id: bucketId,
                    page: 1,
                    // Page size is 2 because we only need to distinguish 0, 1, or 2+ matches
                    // when populating initial Timekeeping row data in the Create Timecard flow.
                    page_size: 2,
                }) as Promise<{ results: Array<{ id: number }> }>
        )
    )
    bucketIdsNeedingLookup.forEach((bucketId, i) => {
        result.set(bucketId, searches[i].results)
    })
    return result
}

export type CreateTimeCardModalProps = {
    // Modal props
    open: boolean
    title?: string
    description?: string
    primaryBtnText?: string
    secondaryBtnOnClick?: () => void
    backgroundCloseEnabled?: boolean
    closeMethod: () => void

    // Additional props
    currentEmployee: tCurrentUserEmployee
    features: tFeatures
    isAdminOrPM: boolean
    setSourceTableLockedColumns: (
        groupKeyInfo: tGroupKeyInfo[],
        tkModalInitialData: tSourceData,
        forceCreateNewTimecard: boolean,
        isCreateFlow: boolean,
        filteredProjects: tProject[]
    ) => void
    columnApi: ColumnApi | any
    context: tContext
    filterState: tFilterState
    referenceableData: tEntitySelection
}

export type CreateTimeCardModalTypes = CreateTimeCardModalProps

const CreateTimeCardModal: FC<CreateTimeCardModalTypes> = props => {
    const defaultShiftStartTime = new Date()
    defaultShiftStartTime.setHours(7)
    defaultShiftStartTime.setMinutes(0)
    defaultShiftStartTime.setSeconds(0)
    defaultShiftStartTime.setMilliseconds(0)
    const defaultShiftEndTime = new Date()
    defaultShiftEndTime.setHours(15)
    defaultShiftEndTime.setMinutes(0)
    defaultShiftEndTime.setSeconds(0)
    defaultShiftEndTime.setMilliseconds(0)

    const [shiftStartTime, setShiftStartTime] = useState<Date | null>(defaultShiftStartTime)
    const [shiftEndTime, setShiftEndTime] = useState<Date | null>(defaultShiftEndTime)
    const [useProjectFilters, setUseProjectFilters] = useState(true)
    const [includeInactive, setIncludeInactive] = useState(false)

    // Ensure that the start date is always earlier than or equal to the end date
    useEffect(() => {
        if (shiftEndTime && shiftStartTime && shiftEndTime < shiftStartTime) {
            // If possible, adjust the date for shiftEndTime while keeping the time,
            // so that you can move a shift date out without having to re-set the times of day
            const updatedShiftEndTime = new Date(shiftStartTime.getTime())
            if (shiftEndTime.getHours() > updatedShiftEndTime.getHours())
                updatedShiftEndTime.setHours(shiftEndTime.getHours())
            setShiftEndTime(updatedShiftEndTime)
        } else if (shiftStartTime && shiftEndTime) {
            // Adjust the shift end date to be the same day as the shift start date if you
            const updatedShiftEndTime = new Date(shiftStartTime.getTime())
            if (shiftEndTime.getHours() > updatedShiftEndTime.getHours())
                updatedShiftEndTime.setHours(shiftEndTime.getHours())
            setShiftEndTime(updatedShiftEndTime)
        }
    }, [shiftStartTime])

    useEffect(() => {
        if (shiftEndTime && shiftStartTime && shiftEndTime < shiftStartTime) {
            setShiftEndTime(shiftStartTime)
        }
    }, [shiftEndTime])

    // Using "any" as a type here because SelectorWrapper's onChange method has the "any" type as well,
    // as there were some issues resolving a specific type when pulling the selector's option.
    // If a change to SelectorWrapper is made to enforce a specific type, this type should be refactored
    // as well.
    const [foreman, setForeman] = useState<any>(props.currentEmployee)
    const [projects, setProjects] = useState<any>([])
    const [employees, setEmployees] = useState<any>(
        props.currentEmployee.user_role === "OFFICE_STAFF" ? [props.currentEmployee] : []
    )
    const [cohorts, setCohorts] = useState<any>([])
    const [costCodes, setCostCodes] = useState<any>([])
    const [addCicoSSTs, setAddCicoSSTs] = useState(true)
    const ptoFulfillment = useSelector(selectPTOFulfillment)

    // shouldClearCache tells the selectors that follow it to clear their cache, for example
    // because another selector that is related to it has been updated
    const [shouldClearCache, setShouldClearCache] = useState(false)
    const [shouldClearProjectCache, setShouldClearProjectCache] = useState(false)
    const [companyStartStopTypes, setCompanyStartStopTypes] = useState<tCompanyStartStopType[]>([])
    const [shiftExtraSchemas, setShiftExtraSchemas] = useState<iEmployeeWorkShiftFormSchema[]>([])
    const shiftDateTimeInterval = props.context.currentUser.company_options.timekeeping_increment ?? 30

    // Every time we load projects, clear the caches of the selectors that filter on project IDs
    useEffect(() => {
        setShouldClearCache(true)
        setCostCodes([])
    }, [projects])

    useEffect(() => {
        setShouldClearProjectCache(true)
    }, [useProjectFilters, includeInactive])

    useEffect(() => {
        API.getRowData("companyStartStopTypes").then(results => {
            setCompanyStartStopTypes(results.results)
        })
        API.getRowData("employeeSchemas").then(results => {
            setShiftExtraSchemas(results.results)
        })
    }, [])

    // A callback function that selectors call after they clear their cache
    const updateClearCache = () => {
        setShouldClearCache(false)
    }

    const updateClearProjectCache = () => {
        setShouldClearProjectCache(false)
    }

    // These formatters format the options in the selectors
    const employeeValueFormatters = referenceablesToValueFormatters["employees"] || {}
    const projectValueFormatters = referenceablesToValueFormatters["projects"] || {}
    const costCodeValueFormatters = referenceablesToValueFormatters["costCodes"] || {}
    const cohortValueFormatters = referenceablesToValueFormatters["cohorts"] || {}

    const timeCardOwnerFilter = {
        exclude_user_role: "WORKER",
    }
    const datetimeFormat = getDateTimeFormats("datetime", false)
    const innerRef = createRef<HTMLDivElement>()

    const populateInitialRowData = (
        shiftDate: string,
        workShift: Record<string, any>,
        cicoData?: iTimelineEntryVersion[],
        cohortEmployees?: number[],
        ptoRecords: EmployeePTORecord[] = [],
        holidayRecords: EmployeeHolidayRecord[] = [],
        ptoCostCodesByBucket: Map<number, Array<{ id: number }>> = new Map()
    ) => {
        const initialRowData: MutableSourceData = {
            timekeepingEntries: [],
            absences: [],
            employeeEntries: [],
            ewsStartStopTimes: [],
            timelineEntryVersions: cicoData,
        }
        const employeeList = new Set([
            ...(cohortEmployees || []),
            ...employees.map((e: iEmployee) => getObjectId(e)),
        ])

        if (!props.features.cico) {
            const missingReferenceables = Array.from(employeeList).filter(
                (eId: number) => !props.referenceableData.employees[eId]
            )
            if (missingReferenceables.length) {
                API.getReferenceableDataByResourceAndIds("employees", missingReferenceables).then(response => {
                    const empPayload = cloneDeep(props.referenceableData.employees)
                    response.results.forEach((emp: iEmployee) => {
                        empPayload[emp.id as number] = emp
                    })
                    props.context.dispatch(
                        loadEntities({
                            employees: cloneDeep(empPayload),
                        })
                    )
                })
            }
        }

        // Pre-group PTO records by employee so the per-employee lookup below is O(1) instead of O(n).
        const ptoByEmployee = getFlagEnabled("WA-9197-pto-accruals")
            ? ([...ptoRecords, ...holidayRecords] as Array<EmployeePTORecord | EmployeeHolidayRecord>).reduce(
                  (map, r) => {
                      if (!map[r.employee]) {
                          map[r.employee] = []
                      }
                      map[r.employee].push(r)
                      return map
                  },
                  {} as Record<number, Array<EmployeePTORecord | EmployeeHolidayRecord>>
              )
            : null

        // Prepare to populate the table with every possible combination of the employees and cost codes
        // that were specified. Users are required to pick at least one of each of these entities,
        // so it's safe just to nest these forEach loops without handling cases for empty arrays
        employeeList.forEach((employee: any) => {
            // TODO: Some of these fields - specifically the adjusted_minutes_* fields - should be populated
            // in addSourceDataRow, which would normally go through
            // and set defaults for all applicable columns on a new row.
            // However, because we're doing all of this before the table fully loads, the colDefs are missing
            // some information (specifically the cellEditorParams) and so those defaults cannot be applied.
            // I hacked in defaults for the three relevant fields but I kind of hate this - it makes the code
            // brittle and feels unnecessary. It would be nice at least to find another way to do this.
            const employeeId = getObjectId(employee)

            costCodes.forEach((costCode: any) => {
                // Skip the standard row if this employee has a PTO/holiday request that will be
                // fulfilled via this cost code's bucket — the PTO row covers it instead.
                if (
                    costCode.pto_bucket_id != null &&
                    ptoByEmployee?.[employeeId]?.some(
                        r => r.pto_bucket_type === "COST_CODE" && r.pto_bucket_id === costCode.pto_bucket_id
                    )
                )
                    return

                const row: Record<string, any> = {
                    employee: employeeId,
                    cost_code: costCode.id,
                    shift_start_time: shiftStartTime,
                    shift_end_time: shiftEndTime,
                    date: shiftDate,
                    foreman: foreman.id,
                    adjusted_minutes_st: 0,
                    adjusted_minutes_ot: 0,
                    adjusted_minutes_dt: 0,
                    work_shift_id: workShift,
                }

                initialRowData.timekeepingEntries?.push(row)

                // Add any per employee/cost code shift extra placeholders
                shiftExtraSchemas
                    .filter(se => se.create_placeholder === "PER_EMPLOYEE_COST_CODE")
                    .forEach(se => {
                        const shiftExtraSettings = getSettingsForShiftExtraSchema(se, props.context)
                        const baseRecord: Record<string, any> = {
                            foreman: foreman.id,
                            employee: employeeId,
                            date: shiftDate,
                            schema: se,
                            shift_start_time: shiftStartTime,
                            shift_end_time: shiftEndTime,
                            work_shift_id: workShift,
                        }
                        if (getFlagEnabled("WA-9382-create-timecard-fixes") && projects.length === 1)
                            baseRecord.project = projects[0]
                        // We need to find any cost code type fields and fill them in with the
                        // current cost code.
                        const shiftExtraFields = populateShiftExtraCostCodeColDef(shiftExtraSettings, costCode.id)
                        initialRowData.employeeEntries?.push({ ...baseRecord, ...shiftExtraFields })
                    })
            })
            const employeeCico = cicoData
                ? cicoData.filter(cd => getObjectId(cd.employee) === getObjectId(employee))
                : []
            if (employeeCico.length) {
                const cicoEventMap: Record<string, tCompanyStartStopType> = {}
                companyStartStopTypes.forEach(sst => {
                    switch (sst.name) {
                        case "Break":
                            cicoEventMap["START_BREAK"] = sst
                            break
                        case "Meal":
                            cicoEventMap["START_MEAL"] = sst
                            break
                        case "Shift":
                            cicoEventMap["CLOCK_IN"] = sst
                            break
                        default:
                            break
                    }
                })
                const shiftDuration = employeeCico.reduce((agg, timelineEntry) => {
                    agg += timelineEntry.duration || 0
                    return agg
                }, 0)
                employeeCico.forEach((timelineEntry: iTimelineEntryVersion) => {
                    if (timelineEntry.entry_type in cicoEventMap) {
                        const duration =
                            timelineEntry.entry_type === "CLOCK_IN" ? shiftDuration : timelineEntry.duration
                        const endTime = duration
                            ? new Date(
                                  new Date(timelineEntry.entered_time).getTime() + duration * 60000
                              ).toISOString()
                            : ""
                        initialRowData.ewsStartStopTimes!.push({
                            foreman: foreman.id,
                            employee: employeeId,
                            company_start_stop_type: cicoEventMap[timelineEntry.entry_type],
                            start_stop_type: cicoEventMap[timelineEntry.entry_type].name,
                            date: shiftDate,
                            start_time: timelineEntry.entered_time,
                            stop_time: endTime,
                            shift_start_time: shiftStartTime,
                            shift_end_time: shiftEndTime,
                            work_shift_id: workShift,
                            isPlaceholder: false,
                            cico_diff: 0,
                        })
                    }
                })
            } else
                companyStartStopTypes.forEach(sst => {
                    const numPlaceholders = sst.num_placeholders || 0
                    for (let i = 0; i < numPlaceholders; i++)
                        initialRowData.ewsStartStopTimes?.push({
                            foreman: foreman.id,
                            employee: employeeId,
                            company_start_stop_type: sst,
                            start_stop_type: sst.name,
                            date: shiftDate,
                            shift_start_time: shiftStartTime,
                            shift_end_time: shiftEndTime,
                            work_shift_id: workShift,
                            cico_diff: 0,
                        })
                })
            shiftExtraSchemas
                .filter(se => se.create_placeholder === "PER_EMPLOYEE")
                .forEach(se => {
                    const baseRecord: Record<string, any> = {
                        foreman: foreman.id,
                        employee: employeeId,
                        date: shiftDate,
                        schema: se,
                        shift_start_time: shiftStartTime,
                        shift_end_time: shiftEndTime,
                        work_shift_id: workShift,
                    }
                    if (getFlagEnabled("WA-9382-create-timecard-fixes") && projects.length === 1)
                        baseRecord.project = projects[0]
                    initialRowData.employeeEntries?.push(baseRecord)
                })

            // Append non-placeholder rows for any PTO/holiday requests on the shift date.
            // These rows have actual hours/absence-type filled in and are added alongside
            // the standard placeholder rows above.
            if (ptoByEmployee) {
                const ptoForEmployee = ptoByEmployee[employeeId] ?? []
                if (ptoForEmployee.length > 0) {
                    const { absences, timekeepingEntries } = buildPTORowsForEmployee(
                        ptoForEmployee,
                        {
                            foreman: foreman.id,
                            shiftStartTime,
                            shiftEndTime,
                            workShift,
                        },
                        ptoCostCodesByBucket
                    )
                    absences.forEach(row => initialRowData.absences?.push(row))
                    timekeepingEntries.forEach(row => initialRowData.timekeepingEntries?.push(row))
                }
            }
        })
        return initialRowData
    }

    const setGroupKeyInfo = (initialRowData: MutableSourceData, workShift: Record<string, any>) => {
        // TODO: Some of this repeats work that can be done in processTimekeepingModalValues - consider
        // refactoring that
        const foremanColDef = props.columnApi.getColumn("/foreman").colDef
        const dateColDef = props.columnApi.getColumn("/date").colDef
        const groupKeyInfo = [
            {
                colDef: dateColDef,
                value: format(shiftStartTime ?? new Date(), STORED_DATE_ONLY_FORMAT),
            },
            {
                colDef: foremanColDef,
                value: foreman,
            },
        ]

        const workshiftColDef = props.columnApi.getColumn("/work_shift_id").colDef
        groupKeyInfo.push({
            colDef: workshiftColDef,
            value: workShift,
        })

        // Open the timekeeping modal, passing in the prepopulated rows
        props.setSourceTableLockedColumns(groupKeyInfo, initialRowData as MutableSourceData, true, true, projects)

        logUserAmplitudeEvent(CREATE_TIME_CARD_MODAL_COMPLETED, {})

        props.closeMethod()
    }

    const onClickSave = () => {
        const shiftDate = format(shiftStartTime ?? new Date(), STORED_DATE_ONLY_FORMAT)
        const workShift = {
            start_time: shiftStartTime,
            end_time: shiftEndTime,
            foreman: foreman,
            foreman_id: foreman.id,
            projects: projects,
            date: shiftDate,
            tempId: 1, // This is a little bit of a hack so that we can edit the timecard before it's saved
            // in the create timecard flow. this will go away when we allow for unsaved timecards in the
            // selector
        }

        if (cohorts.length) {
            API.getV4Resources("cohortEmployees", {
                cohort_ids: cohorts.map((c: iCohort) => c.id),
                filter_employee_access: true,
            }).then(response => {
                createInitialData(
                    shiftDate,
                    workShift,
                    response.results.map((r: iCohortEmployee) => r.employee)
                )
            })
        } else {
            createInitialData(shiftDate, workShift)
        }
    }

    const createInitialData = async (
        shiftDate: string,
        workShift: Record<string, any>,
        cohortEmployees?: number[]
    ) => {
        const allEmployeeIds = [...new Set([...(cohortEmployees ?? []), ...employees.map((e: iEmployee) => e.id)])]

        const cicoProm = addCicoSSTs
            ? (API.getV4Resources("timelineEntryVersions", {
                  shift_start_time_0: shiftDate,
                  shift_start_time_1: shiftDate,
                  for_dashboard: true,
                  employee_id: [employees.map((e: iEmployee) => e.id)],
              }) as Promise<{ results: iTimelineEntryVersion[] }>)
            : null

        const ptoProm = getFlagEnabled("WA-9197-pto-accruals")
            ? Promise.all([
                  API.getV4Resources("employeePTO", {
                      shift_start_time_0: shiftDate,
                      shift_start_time_1: shiftDate,
                      employee_id: allEmployeeIds,
                  }) as Promise<{ results: EmployeePTORecord[] }>,
                  API.getV4Resources("employeeHolidays", {
                      shift_start_time_0: shiftDate,
                      shift_start_time_1: shiftDate,
                      employee_id: allEmployeeIds,
                  }) as Promise<{ results: EmployeeHolidayRecord[] }>,
              ]).catch(() => null)
            : null

        const [cicoResponse, ptoResult] = await Promise.all([cicoProm, ptoProm])

        let ptoRecords: EmployeePTORecord[] = []
        let holidayRecords: EmployeeHolidayRecord[] = []
        if (ptoResult) {
            ptoRecords = ptoResult[0].results
            holidayRecords = ptoResult[1].results
            // Seed the Redux cache so the PTO Requests tab renders the data immediately.
            props.context.dispatch(
                loadEntities({
                    employeePTO: Object.fromEntries(ptoRecords.map(r => [r.id, r])),
                    employeeHolidays: Object.fromEntries(holidayRecords.map(r => [r.id, r])),
                })
            )
            // Filter out already-fulfilled requests so we only pre-populate rows for unfulfilled ones.
            ptoRecords = ptoRecords.filter(r => !ptoFulfillment[String(r.id)])
            holidayRecords = holidayRecords.filter(r => !ptoFulfillment[String(r.id)])
        }

        // For COST_CODE bucket PTO records, search for matching cost codes on the selected
        // projects so the user doesn't have to manually select them.
        let ptoCostCodesByBucket: Map<number, Array<{ id: number }>> = new Map()
        if (getFlagEnabled("WA-9197-pto-accruals")) {
            try {
                ptoCostCodesByBucket = await buildPTOCostCodesByBucket(
                    [...ptoRecords, ...holidayRecords],
                    projects.map((p: tProject) => p.id),
                    costCodes
                )
            } catch {
                // A cost-code lookup failure is non-fatal; the user can select cost codes manually.
            }
        }

        const initialRowData = populateInitialRowData(
            shiftDate,
            workShift,
            cicoResponse?.results.reverse(),
            cohortEmployees,
            ptoRecords,
            holidayRecords,
            ptoCostCodesByBucket
        )
        setGroupKeyInfo(initialRowData, workShift)
    }

    const onClickCancel = () => {
        logUserAmplitudeEvent(CREATE_TIME_CARD_MODAL_CANCELLED, {})

        props.closeMethod()
    }

    const getLabelText = (elementCount: number, singularEntityName: string, pluralEntityName: string) => {
        return elementCount ? `${pluralEntityName} (${elementCount})` : pluralEntityName
    }

    return (
        <StandardModal
            {...props}
            title={"Create a Time Card"}
            description={
                "Select the projects, workers, and cost codes for the time card you'd like to create. " +
                "The next step will be to add hours to the time card."
            }
            width={"90vw"}
            theme={"formModal"}
            primaryBtnColor={colorAttentionBlue50}
            primaryBtnOnClick={onClickSave}
            primaryBtnDisabled={
                !(
                    projects.length > 0 &&
                    (employees.length > 0 || cohorts.length > 0) &&
                    costCodes.length > 0 &&
                    foreman
                )
            }
            secondaryBtnOnClick={onClickCancel}
        >
            <Wrapper scrollable={true}>
                <FormFieldGroup>
                    <Label htmlFor="time-card-owner-selector" isRequired={true}>
                        Time Card Owner
                    </Label>
                    <SelectorWrapper
                        id="time-card-owner-selector"
                        isDisabled={!adminAndPmRoles.has(props.currentEmployee.user_role)}
                        innerRef={innerRef}
                        isMulti={false}
                        resourceName={"employees"}
                        onChange={setForeman}
                        value={foreman}
                        filters={timeCardOwnerFilter}
                        valueFormatter={employeeValueFormatters.valueFormatter}
                        titleFormatter={employeeValueFormatters.titleFormatter}
                        primarySubtitleFormatter={employeeValueFormatters.primarySubtitleFormatter}
                        secondarySubtitleFormatter={employeeValueFormatters.secondarySubtitleFormatter}
                    />
                </FormFieldGroup>
                <FormFieldGroup>
                    <div style={{ marginTop: 10, marginBottom: 0 }}>
                        <Label htmlFor="date" isRequired={true}>
                            Shift Date
                        </Label>
                        {props.features.disable_work_shift_start_stop_times ? (
                            <div className="date-pickers" style={{ marginBottom: 16, width: 250 }}>
                                <DatePicker
                                    id="work-shift-date"
                                    className="form-control date-filter-input"
                                    dateFormat={DATE_ONLY_FORMAT}
                                    onChange={(date: Date) => setShiftStartTime(date)}
                                    selected={shiftStartTime}
                                    showTimeSelect={false}
                                />
                            </div>
                        ) : (
                            <div className="date-pickers">
                                <DatePicker
                                    id="start-date"
                                    className="form-control date-filter-input"
                                    dateFormat={datetimeFormat.dateFormat}
                                    onChange={(date: Date) => setShiftStartTime(date)}
                                    selected={shiftStartTime}
                                    timeIntervals={shiftDateTimeInterval}
                                    showTimeSelect={true}
                                />{" "}
                                <div className="date-filter-to">
                                    <p> — </p>
                                </div>
                                <DatePicker
                                    id="end-date"
                                    className="form-control date-filter-input"
                                    dateFormat={datetimeFormat.dateFormat}
                                    onChange={(date: Date) => setShiftEndTime(date)}
                                    selected={shiftEndTime}
                                    minDate={
                                        getFlagEnabled("WA-9382-create-timecard-fixes") && shiftStartTime
                                            ? startOfDay(shiftStartTime)
                                            : undefined
                                    }
                                    minTime={
                                        getFlagEnabled("WA-9382-create-timecard-fixes") && shiftStartTime
                                            ? startOfDay(shiftStartTime)
                                            : undefined
                                    }
                                    maxTime={
                                        getFlagEnabled("WA-9382-create-timecard-fixes") && shiftStartTime
                                            ? endOfDay(shiftStartTime)
                                            : undefined
                                    }
                                    maxDate={
                                        getFlagEnabled("WA-9382-create-timecard-fixes") && shiftStartTime
                                            ? addDays(shiftStartTime, 1)
                                            : undefined
                                    }
                                    timeIntervals={shiftDateTimeInterval}
                                    showTimeSelect={true}
                                />
                            </div>
                        )}
                        {props.features.cico && (
                            <AddCicoSSTs>
                                <div style={{ marginRight: 10 }}>
                                    <Checkbox
                                        id={"add-cico-ssts"}
                                        checked={addCicoSSTs}
                                        onChange={() => setAddCicoSSTs(!addCicoSSTs)}
                                    />
                                </div>
                                <InheritFiltersLabel>
                                    <Label htmlFor={"add-cico-ssts"}>Add Shift & Break rows for CI/CO data</Label>
                                </InheritFiltersLabel>
                            </AddCicoSSTs>
                        )}
                    </div>
                </FormFieldGroup>
                <FormFieldGroup>
                    <Label htmlFor="projects-selector" isRequired={true}>
                        {getLabelText(projects.length, "Project", "Projects")}
                    </Label>
                    <SelectorWrapper
                        id="projects-selector"
                        innerRef={innerRef}
                        isMulti={true}
                        resourceName={"projects"}
                        onChange={setProjects}
                        value={projects}
                        filters={
                            useProjectFilters
                                ? {
                                      group_id: props.filterState.groupId,
                                      project_status: includeInactive ? null : "ACTIVE",
                                      project_id: props.filterState.projectId,
                                  }
                                : {
                                      project_status: !includeInactive ? "ACTIVE" : null,
                                  }
                        }
                        shouldClearCache={shouldClearProjectCache}
                        updateClearCache={updateClearProjectCache}
                        valueFormatter={projectValueFormatters.valueFormatter}
                        titleFormatter={projectValueFormatters.titleAndStatusFormatter}
                        primarySubtitleFormatter={projectValueFormatters.primarySubtitleFormatter}
                        secondarySubtitleFormatter={projectValueFormatters.secondarySubtitleFormatter}
                    />

                    <ProjectLabelContainer>
                        <InheritFilters>
                            <InheritFiltersLabel>
                                <Checkbox
                                    id={"include-inactive-projects"}
                                    checked={includeInactive}
                                    onChange={() => setIncludeInactive(!includeInactive)}
                                />
                            </InheritFiltersLabel>
                            <InheritFiltersLabel>
                                <Label htmlFor={"use-project-filters"}>Include Inactive Projects</Label>
                            </InheritFiltersLabel>
                            <InheritFiltersLabel>
                                <Checkbox
                                    id={"use-project-filters"}
                                    checked={useProjectFilters}
                                    onChange={() => setUseProjectFilters(!useProjectFilters)}
                                />
                            </InheritFiltersLabel>
                            <InheritFiltersLabel>
                                <Label htmlFor={"use-project-filters"}>Apply Filters from Weekly View</Label>
                            </InheritFiltersLabel>
                        </InheritFilters>
                    </ProjectLabelContainer>
                </FormFieldGroup>
                <FormFieldGroup>
                    <Label htmlFor="cohorts-selector" isRequired={false}>
                        {getLabelText(cohorts.length, "Cohort", "Cohorts")}
                    </Label>
                    <SelectorWrapper
                        id="cohorts-selector"
                        innerRef={innerRef}
                        isDisabled={props.currentEmployee.user_role === "OFFICE_STAFF"}
                        isMulti={true}
                        menuPlacement={"top"}
                        resourceName={"cohorts"}
                        onChange={setCohorts}
                        value={cohorts}
                        filters={{ remove_empty: true }}
                        valueFormatter={cohortValueFormatters.valueFormatter}
                        titleFormatter={cohortValueFormatters.titleFormatter}
                    />
                </FormFieldGroup>
                <FormFieldGroup>
                    <Label htmlFor="employees-selector" isRequired={true}>
                        {getLabelText(employees.length, "Employee", "Employees")}
                    </Label>
                    <SelectorWrapper
                        id="employees-selector"
                        innerRef={innerRef}
                        isMulti={true}
                        resourceName={"employees"}
                        onChange={setEmployees}
                        value={employees}
                        filters={[]}
                        menuPlacement={"top"}
                        valueFormatter={employeeValueFormatters.valueFormatter}
                        titleFormatter={employeeValueFormatters.titleFormatter}
                        primarySubtitleFormatter={employeeValueFormatters.primarySubtitleFormatter}
                        secondarySubtitleFormatter={employeeValueFormatters.secondarySubtitleFormatter}
                    />
                </FormFieldGroup>
                <FormFieldGroup>
                    <Label htmlFor="cost-code-selector" isRequired={true}>
                        {getLabelText(costCodes.length, "Cost Code", "Cost Codes")}
                    </Label>
                    <SelectorWrapper
                        id="cost-code-selector"
                        innerRef={innerRef}
                        isMulti={true}
                        resourceName={"costCodes"}
                        onChange={setCostCodes}
                        value={costCodes}
                        filters={{
                            minimal_project_detail: "true",
                            project_id: [projects.map((proj: any) => proj.id)],
                        }}
                        menuPlacement={"top"}
                        valueFormatter={costCodeValueFormatters.valueFormatter}
                        titleFormatter={costCodeValueFormatters.titleFormatter}
                        primarySubtitleFormatter={costCodeValueFormatters.primarySubtitleFormatter}
                        secondarySubtitleFormatter={costCodeValueFormatters.secondarySubtitleFormatter}
                        shouldClearCache={shouldClearCache}
                        updateClearCache={updateClearCache}
                    />
                </FormFieldGroup>
            </Wrapper>
        </StandardModal>
    )
}

interface WrapperProps {
    scrollable: boolean
}

const Wrapper: FC<WrapperProps> = ({ children, scrollable }) => {
    if (scrollable) {
        return (
            <ScrollableWrapper>
                <ScrollPadding>{children}</ScrollPadding>
            </ScrollableWrapper>
        )
    }
    return <div style={{ paddingTop: "16px" }}>{children}</div>
}

export default CreateTimeCardModal

const ProjectLabelContainer = styled.div`
    display: flex;
    justify-content: space-between;
    margin-top: 15px;
    margin-bottom: -15px;
`

const InheritFilters = styled.div`
    display: flex;
`

const InheritFiltersLabel = styled.div`
    margin-right: 8px;
`

const ScrollableWrapper = styled.div`
    padding-top: 16px;
    padding-right: 16px;
    max-height: 90vh;
    overflow-y: scroll;
    overflow-x: clip;
`

const ScrollPadding = styled.div`
    padding-bottom: 5px;
`

const AddCicoSSTs = styled.div`
    display: flex;
    justify-content: flex-start;
    margin-top: 15px;
    margin-bottom: -15px;
`
